import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Link, useParams, useSearch } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ImagePlus, Loader2, Trash2, Expand, ArrowLeft } from "lucide-react";
import type { TemplateStudioType } from "@shared/schema";
import type { CreativeAdRow } from "@shared/creative-performance";
import {
  DEFAULT_MIN_SPEND, rankTopPerformers, selectTopPerformerMetric, topPerformerValue,
} from "@shared/creative-performance";
import { formatPerformanceMetricLabel } from "@/lib/concept-board-reference-selection";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  dateRangeForWindow, type ImportStatus, type LibraryImage, type PerformanceConfig,
  type SuggestionState, type TypeSuggestion, type TopPerformerWindow,
} from "@/lib/template-studio-library";
import { LibraryTypeSuggestions } from "@/components/template-studio-library/type-suggestions";
import { TopPerformersPanel } from "@/components/template-studio-library/top-performers-panel";
import { ReferencePagination, useReferencePagination } from "@/components/template-studio-library/reference-pagination";
import { ReferenceImage } from "@/components/template-studio-library/reference-image";
import { MyAdsPanel } from "@/components/template-studio-library/my-ads-panel";
import { ConceptBoardsWorkspace, ConceptBoardsWorkspaceHeader } from "@/components/concept-boards-workspace";

async function request<T = unknown>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "include", ...options });
  const body = response.status === 204 ? null : await response.json().catch(() => ({}));
  if (!response.ok) {
    const payload = body as { message?: string; error?: string } | null;
    throw new Error(payload?.message || payload?.error || `Request failed (${response.status})`);
  }
  return body as T;
}

export default function TemplateStudioLibraryPage() {
  const { brandId = "" } = useParams<{ brandId: string }>();
  const search = useSearch();
  const typeId = new URLSearchParams(search).get("typeId");
  return <ConceptBoardsWorkspace>
    <ConceptBoardsWorkspaceHeader title="Board References" description="Collect inspiration images for this brand and assign them to board types.">
      <Button variant="outline" asChild><Link href={`/brands/${encodeURIComponent(brandId)}/template-studio/types`}><ArrowLeft className="mr-2 h-4 w-4" />Back to Board Types</Link></Button>
    </ConceptBoardsWorkspaceHeader>
    <BoardReferencesWorkspace brandId={brandId} selectedTypeId={typeId} />
  </ConceptBoardsWorkspace>;
}

type WorkspaceProps = { brandId: string; selectedTypeId?: string | null; embedded?: boolean };

/** Remount only for a brand change, never for type selection or source switching. */
export function BoardReferencesWorkspace(props: WorkspaceProps) {
  return <ReferenceWorkspace key={props.brandId} {...props} />;
}

function ReferenceWorkspace({ brandId, selectedTypeId = null, embedded = false }: WorkspaceProps) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const importLockRef = useRef(false);
  const libraryActionLocksRef = useRef(new Set<string>());
  const brandFenceRef = useRef({ brandId, generation: 0 });
  if (brandFenceRef.current.brandId !== brandId) {
    brandFenceRef.current = { brandId, generation: brandFenceRef.current.generation + 1 };
  }
  const context = selectedTypeId ?? "";
  const [filterChoice, setFilterChoice] = useState({ context, value: context });
  const filter = filterChoice.context === context ? filterChoice.value : context;
  const setFilter = (value: string) => setFilterChoice({ context, value });
  useEffect(() => {
    setFilterChoice((previous) => previous.context === context ? previous : { context, value: context });
  }, [context]);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      brandFenceRef.current = { ...brandFenceRef.current, generation: brandFenceRef.current.generation + 1 };
    };
  }, []);
  const [dragging, setDragging] = useState(false);
  const [tab, setTab] = useState("library");
  const [windowDays, setWindowDays] = useState<TopPerformerWindow>(30);
  const [selectedAds, setSelectedAds] = useState<string[]>([]);
  const [importsBusy, setImportsBusy] = useState(false);
  const [cardTypeIds, setCardTypeIds] = useState<Record<string, string>>({});
  const [importStatuses, setImportStatuses] = useState<Record<string, ImportStatus>>({});
  const [importSummary, setImportSummary] = useState("");
  const [libraryActionKeys, setLibraryActionKeys] = useState<string[]>([]);
  const [libraryEnabledErrors, setLibraryEnabledErrors] = useState<Record<string, string>>({});
  const [librarySuggestions, setLibrarySuggestions] = useState<Record<string, SuggestionState | undefined>>({});
  const [confirmingLibraryIds, setConfirmingLibraryIds] = useState<string[]>([]);
  const [performerSuggestions, setPerformerSuggestions] = useState<Record<string, SuggestionState | undefined>>({});
  const [suggestionEpochs, setSuggestionEpochs] = useState<Record<string, number>>({});
  const suggestionEpochsRef = useRef(suggestionEpochs);
  suggestionEpochsRef.current = suggestionEpochs;
  const base = `/api/brands/${encodeURIComponent(brandId)}/template-studio/library-images`;
  const importContextKey = `${brandId}:${windowDays}`;
  const importContextRef = useRef({ key: importContextKey, generation: 0 });
  if (importContextRef.current.key !== importContextKey) {
    importContextRef.current = { key: importContextKey, generation: importContextRef.current.generation + 1 };
  }

  useEffect(() => {
    setSelectedAds([]);
    setCardTypeIds({});
    setImportStatuses({});
    setImportSummary("");
    setLibraryActionKeys([]);
    setLibraryEnabledErrors({});
    setLibrarySuggestions({});
    setConfirmingLibraryIds([]);
    setPerformerSuggestions({});
    setSuggestionEpochs({});
    setTab("library");
    setWindowDays(30);
  }, [brandId]);

  const { data: types, isError: typesError, isLoading: typesLoading, refetch: refetchTypes } = useQuery<TemplateStudioType[]>({
    queryKey: ["/api/template-studio/types", brandId],
    queryFn: () => request(`/api/template-studio/types?brandId=${encodeURIComponent(brandId)}`),
    enabled: !!brandId,
    retry: false,
  });
  const available = types?.filter((type) => type.enabled) || [];
  // The existing API accepts enabled types only in its typeId read filter.
  // Archived memberships remain readable via the same brand-authorized full
  // library, without relaxing assignment/import admission on the server.
  const disabledTypeFilter = !!types?.some((type) => type.id === filter && !type.enabled);
  const requestedTypeId = filter === "unassigned" || disabledTypeFilter ? "" : filter;
  const imagesKey = [base, requestedTypeId];
  const { data: imageRows, isLoading, isError, refetch } = useQuery<LibraryImage[]>({
    queryKey: imagesKey,
    queryFn: () => request(`${base}${requestedTypeId ? `?typeId=${encodeURIComponent(requestedTypeId)}` : ""}`),
    enabled: !!brandId && !typesLoading && !typesError,
    retry: false,
  });
  const images = useMemo(
    () => filter === "unassigned" ? imageRows?.filter((image) => image.typeIds.length === 0)
      : disabledTypeFilter ? imageRows?.filter((image) => image.typeIds.includes(filter)) : imageRows,
    [filter, disabledTypeFilter, imageRows],
  );
  const libraryPagination = useReferencePagination(images ?? [], `${brandId}:${filter}`);
  const {
    data: memberships = [], isLoading: membershipsLoading, isError: membershipsError, refetch: refetchMemberships,
  } = useQuery<LibraryImage[]>({
    queryKey: [base, "membership"],
    queryFn: () => request(base),
    enabled: !!brandId,
    retry: false,
  });
  const refreshLibrary = () => queryClient.invalidateQueries({ queryKey: [base] });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("file", file);
      const result = await request(base, { method: "POST", body });
      await queryClient.invalidateQueries({ queryKey: [base] });
      return result;
    },
    onSuccess: () => { if (mountedRef.current) toast({ title: "Reference uploaded" }); },
    onError: (error: Error) => { if (mountedRef.current) toast({ title: "Could not upload image", description: error.message, variant: "destructive" }); },
  });
  const remove = useMutation({
    mutationFn: async (image: LibraryImage) => {
      const result = await request(`${base}/${image.id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: [base] });
      return result;
    },
    onSuccess: () => { if (mountedRef.current) toast({ title: "Reference removed" }); },
    onError: (error: Error) => { if (mountedRef.current) toast({ title: "Could not remove reference", description: error.message, variant: "destructive" }); },
  });

  const configUrl = `/api/brands/${encodeURIComponent(brandId)}/creative-performance/config`;
  const performanceConfig = useQuery<PerformanceConfig>({
    queryKey: [configUrl],
    queryFn: ({ signal }) => request(configUrl, { signal }),
    enabled: !!brandId,
    retry: false,
    placeholderData: () => undefined,
    staleTime: 0,
    refetchOnMount: "always",
  });
  // Do not admit cached/placeholder settings while the current brand's
  // configuration is being confirmed, or retain access after a failed refresh.
  const performanceEnabled = !!brandId && performanceConfig.isSuccess &&
    !performanceConfig.isFetching && !performanceConfig.isPlaceholderData &&
    performanceConfig.data?.enabled === true;
  const performanceFenceRef = useRef({ enabled: performanceEnabled, generation: 0 });
  if (performanceFenceRef.current.enabled !== performanceEnabled) {
    performanceFenceRef.current = {
      enabled: performanceEnabled, generation: performanceFenceRef.current.generation + 1,
    };
  }
  const activeTab = tab === "top-performers" && !performanceEnabled ? "library" : tab;
  useEffect(() => {
    if (performanceEnabled) return;
    setTab((previous) => previous === "top-performers" ? "library" : previous);
    setSelectedAds([]);
    setCardTypeIds({});
    setImportStatuses({});
    setImportSummary("");
    setPerformerSuggestions({});
  }, [performanceEnabled]);
  const performanceRange = useMemo(() => dateRangeForWindow(windowDays), [windowDays]);
  const adsUrl = `/api/brands/${encodeURIComponent(brandId)}/creative-performance/ads?since=${performanceRange.since}&until=${performanceRange.until}&tier=All`;
  const performanceAds = useQuery<CreativeAdRow[]>({
    queryKey: [adsUrl],
    queryFn: ({ signal }) => {
      if (!performanceFenceRef.current.enabled) throw new Error("Creative Performance is unavailable.");
      return request(adsUrl, { signal });
    },
    enabled: performanceEnabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const metric = useMemo(() => selectTopPerformerMetric(
    performanceAds.data ?? [], performanceConfig.data?.resolvedLeadEventName ?? null,
  ), [performanceAds.data, performanceConfig.data?.resolvedLeadEventName]);
  const topPerformers = useMemo(() => rankTopPerformers(performanceAds.data ?? [], {
    metric, minSpend: DEFAULT_MIN_SPEND,
  }), [performanceAds.data, metric]);
  const performerPagination = useReferencePagination(topPerformers, `${brandId}:${windowDays}`);
  const membershipByAd = useMemo(() => new Map(
    memberships.filter((image) => image.externalAdId).map((image) => [image.externalAdId!, image]),
  ), [memberships]);

  function beginLibraryAction(imageId: string) {
    const fence = { ...brandFenceRef.current };
    const key = `${fence.generation}:${fence.brandId}:${imageId}`;
    if (libraryActionLocksRef.current.has(key)) return null;
    libraryActionLocksRef.current.add(key);
    setLibraryActionKeys((previous) => [...previous, key]);
    return { fence, key, base };
  }

  function isCurrentLibraryAction(action: { fence: { brandId: string; generation: number } }) {
    return brandFenceRef.current.brandId === action.fence.brandId &&
      brandFenceRef.current.generation === action.fence.generation;
  }

  function finishLibraryAction(action: { fence: { brandId: string; generation: number }; key: string }) {
    libraryActionLocksRef.current.delete(action.key);
    if (isCurrentLibraryAction(action)) {
      setLibraryActionKeys((previous) => previous.filter((key) => key !== action.key));
    }
  }

  function libraryActionPending(imageId: string) {
    const key = `${brandFenceRef.current.generation}:${brandId}:${imageId}`;
    return libraryActionKeys.includes(key);
  }

  async function updateLibraryEnabled(image: LibraryImage, enabled: boolean) {
    const action = beginLibraryAction(image.id);
    if (!action) return;
    setLibraryEnabledErrors((previous) => ({ ...previous, [image.id]: "" }));
    try {
      await request(`${action.base}/${image.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      await queryClient.invalidateQueries({ queryKey: [action.base] });
    } catch (error) {
      if (isCurrentLibraryAction(action)) {
        setLibraryEnabledErrors((previous) => ({
          ...previous,
          [image.id]: (error as Error).message,
        }));
      }
    } finally {
      finishLibraryAction(action);
    }
  }

  async function classifyImage(image: LibraryImage, typeIds: string[]) {
    const action = beginLibraryAction(image.id);
    if (!action) return;
    bumpSuggestionEpoch(`library:${image.id}`);
    try {
      await request(`${action.base}/${image.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typeIds }),
      });
      await queryClient.invalidateQueries({ queryKey: [action.base] });
    } catch (error) {
      if (isCurrentLibraryAction(action)) {
        toast({ title: "Could not update types", description: (error as Error).message, variant: "destructive" });
      }
    } finally {
      finishLibraryAction(action);
    }
  }

  function bumpSuggestionEpoch(key: string) {
    const next = { ...suggestionEpochsRef.current, [key]: (suggestionEpochsRef.current[key] || 0) + 1 };
    suggestionEpochsRef.current = next;
    setSuggestionEpochs(next);
    if (key.startsWith("library:")) {
      const id = key.slice("library:".length);
      setLibrarySuggestions((previous) => ({ ...previous, [id]: undefined }));
    } else {
      const adId = key.slice("performer:".length);
      setPerformerSuggestions((previous) => ({ ...previous, [adId]: undefined }));
    }
  }

  async function suggestTypes(target: "library" | "performer", id: string, body: { imageId: string } | { token: string }) {
    if (target === "performer" && !performanceFenceRef.current.enabled) return;
    const performanceGeneration = performanceFenceRef.current.generation;
    const performerUnavailable = () => target === "performer" &&
      (!performanceFenceRef.current.enabled || performanceFenceRef.current.generation !== performanceGeneration);
    const action = target === "library" ? beginLibraryAction(id) : null;
    if (target === "library" && !action) return;
    const key = `${target}:${id}`;
    const capturedFence = { ...brandFenceRef.current };
    const epoch = suggestionEpochsRef.current[key] || 0;
    const setter = target === "library" ? setLibrarySuggestions : setPerformerSuggestions;
    setter((previous) => ({ ...previous, [id]: { loading: true } }));
    try {
      const result = await request<{ suggestions: TypeSuggestion[] }>(`${base}/suggest-types`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (performerUnavailable() || brandFenceRef.current.brandId !== capturedFence.brandId ||
        brandFenceRef.current.generation !== capturedFence.generation ||
        (suggestionEpochsRef.current[key] || 0) !== epoch) return;
      setter((previous) => ({ ...previous, [id]: { suggestions: Array.isArray(result.suggestions) ? result.suggestions : [] } }));
    } catch (error) {
      if (performerUnavailable() || brandFenceRef.current.brandId !== capturedFence.brandId ||
        brandFenceRef.current.generation !== capturedFence.generation ||
        (suggestionEpochsRef.current[key] || 0) !== epoch) return;
      setter((previous) => ({ ...previous, [id]: { error: (error as Error).message } }));
    } finally {
      if (action) finishLibraryAction(action);
    }
  }

  async function confirmLibrarySuggestion(image: LibraryImage, typeId: string) {
    const action = beginLibraryAction(image.id);
    if (!action) return;
    const suggestionKey = `library:${image.id}`;
    const capturedEpoch = suggestionEpochsRef.current[suggestionKey] || 0;
    setConfirmingLibraryIds((previous) => [...new Set([...previous, image.id])]);
    try {
      if (!isCurrentLibraryAction(action)) return;
      if ((suggestionEpochsRef.current[suggestionKey] || 0) !== capturedEpoch) return;
      await request(`${action.base}/${image.id}/add-type`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ typeId }),
      });
      await queryClient.invalidateQueries({ queryKey: [action.base] });
      if (!isCurrentLibraryAction(action)) return;
      bumpSuggestionEpoch(`library:${image.id}`);
      setLibrarySuggestions((previous) => ({ ...previous, [image.id]: undefined }));
      if (isCurrentLibraryAction(action)) toast({ title: "Board type assigned" });
    } catch (error) {
      if (isCurrentLibraryAction(action)) {
        toast({ title: "Could not assign board type", description: (error as Error).message, variant: "destructive" });
      }
    } finally {
      finishLibraryAction(action);
      if (isCurrentLibraryAction(action)) {
        setConfirmingLibraryIds((previous) => previous.filter((id) => id !== image.id));
      }
    }
  }

  function setPerformerType(adId: string, typeId: string) {
    if (!performanceFenceRef.current.enabled) return;
    setCardTypeIds((previous) => ({ ...previous, [adId]: typeId }));
    bumpSuggestionEpoch(`performer:${adId}`);
  }

  async function confirmPerformerSuggestion(adId: string, typeId: string) {
    if (!performanceFenceRef.current.enabled) return;
    setPerformerType(adId, typeId);
    setPerformerSuggestions((previous) => ({ ...previous, [adId]: undefined }));
  }

  async function addSelectedToLibrary() {
    if (!performanceFenceRef.current.enabled || importLockRef.current || !selectedAds.length || selectedAds.some((id) => !cardTypeIds[id])) return;
    importLockRef.current = true;
    setImportsBusy(true);
    const fence = { ...brandFenceRef.current };
    const importFence = { ...importContextRef.current };
    const performanceGeneration = performanceFenceRef.current.generation;
    const isCurrentImport = () => performanceFenceRef.current.enabled &&
      performanceFenceRef.current.generation === performanceGeneration &&
      mountedRef.current && brandFenceRef.current.brandId === fence.brandId &&
      brandFenceRef.current.generation === fence.generation &&
      importContextRef.current.key === importFence.key &&
      importContextRef.current.generation === importFence.generation;
    const selection = [...selectedAds];
    const adsById = new Map(topPerformers.map((ad) => [ad.adId, ad]));
    setImportSummary("");
    let added = 0;
    let failed = 0;
    const succeededIds: string[] = [];
    try {
    for (const adId of selection) {
      if (!isCurrentImport()) break;
      const ad = adsById.get(adId);
      const typeId = cardTypeIds[adId];
      if (!ad || !typeId) continue;
      setImportStatuses((previous) => ({ ...previous, [adId]: { state: "pending" } }));
      try {
        if (!ad.iterationSourceToken) throw new Error("This ad no longer has a usable signed image source.");
        const value = topPerformerValue(ad, metric);
        await request(`${base}/from-performance`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: ad.iterationSourceToken,
            typeIds: [typeId],
            snapshot: {
              metric, value, spend: ad.spend, costPer: value > 0 ? ad.spend / value : null,
              since: performanceRange.since, until: performanceRange.until,
            },
          }),
        });
        await refreshLibrary();
        if (!isCurrentImport()) break;
        added++;
        succeededIds.push(adId);
        setImportStatuses((previous) => ({ ...previous, [adId]: { state: "success" } }));
      } catch (error) {
        if (!isCurrentImport()) break;
        failed++;
        setImportStatuses((previous) => ({ ...previous, [adId]: { state: "error", error: (error as Error).message } }));
      }
    }
    if (isCurrentImport()) {
      setImportSummary(`${added} added or updated${failed ? `, ${failed} failed` : ""}.`);
      if (added) {
        await refreshLibrary();
        setSelectedAds((previous) => previous.filter((id) => !succeededIds.includes(id)));
      }
    }
    } finally {
      importLockRef.current = false;
      if (mountedRef.current) setImportsBusy(false);
    }
  }

  const pick = (file?: File) => {
    if (file) upload.mutate(file);
    if (inputRef.current) inputRef.current.value = "";
  };
  const dropped = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    pick(event.dataTransfer.files[0]);
  };
  const chosen = (event: ChangeEvent<HTMLInputElement>) => pick(event.target.files?.[0]);

  return (
    <section aria-label="Reference library" data-testid={embedded ? "embedded-reference-workspace" : "expanded-reference-workspace"}
      className={`min-w-0 space-y-4 ${embedded ? "rounded-xl border border-slate-200 bg-white p-3 sm:p-4" : ""}`}>
      {embedded && <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0"><h2 className="text-base font-semibold">Reference library</h2>
          <p className="mt-1 text-xs text-muted-foreground">Collect inspiration images and manage their board types.</p></div>
        <Button variant="outline" size="sm" asChild><Link href={`/brands/${encodeURIComponent(brandId)}/template-studio/library${filter ? `?typeId=${encodeURIComponent(filter)}` : ""}`}><Expand className="mr-2 h-3.5 w-3.5" />Expand library</Link></Button>
      </div>}

      <Card className="border-violet-100 bg-white shadow-[0_8px_28px_-24px_rgba(50,34,90,.35)]">
        <CardContent className={embedded ? "p-2" : "p-3 sm:p-4"}>
          <div onDragOver={(event) => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={dropped}
            className={`items-center gap-2 rounded-lg border border-dashed px-3 transition-colors ${embedded ? "grid grid-cols-[auto_minmax(0,1fr)] py-2 text-left sm:flex sm:flex-wrap" : "flex flex-col py-5 text-center sm:py-6"} ${dragging ? "border-violet-500 bg-violet-50" : "border-violet-200 bg-[#fbfaff]"}`}>
            <ImagePlus className={`${embedded ? "h-4 w-4 shrink-0" : "h-6 w-6"} text-violet-600`} />
            <p className={`text-xs text-slate-600 ${embedded ? "min-w-0 flex-1" : "sm:text-sm"}`}>Drop an image here or choose a file. JPEG, PNG, WebP, GIF or AVIF, up to 10 MB.</p>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" className="sr-only" onChange={chosen} aria-label="Choose reference image" />
            <Button type="button" size="sm" className={`${embedded ? "col-span-2 w-full sm:w-auto" : ""} shrink-0 bg-violet-700 text-white hover:bg-violet-800`} disabled={upload.isPending} onClick={() => inputRef.current?.click()}>
              {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
              Upload reference
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={(value) => {
        if (value !== "top-performers" || performanceFenceRef.current.enabled) setTab(value);
      }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
        <TabsList aria-label="Reference source" className="h-auto rounded-lg border border-violet-200 bg-white p-1">
           <TabsTrigger value="library" className="rounded-md px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-violet-700 data-[state=active]:text-white data-[state=active]:shadow-none">Library</TabsTrigger>
           <TabsTrigger value="my-ads" data-testid="tab-my-ads" className="rounded-md px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-violet-700 data-[state=active]:text-white data-[state=active]:shadow-none">My Ads</TabsTrigger>
            {performanceEnabled && <TabsTrigger value="top-performers" data-testid="tab-top-performers" className="rounded-md px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-violet-700 data-[state=active]:text-white data-[state=active]:shadow-none">Top performers</TabsTrigger>}
        </TabsList>
          {activeTab === "library" && <div className="flex min-w-0 max-w-full items-center gap-2">
            <Label htmlFor="board-reference-filter" className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Show</Label>
            <select id="board-reference-filter" className="h-9 min-w-0 max-w-64 rounded-md border border-violet-200 bg-white px-3 text-sm text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500" value={filter} onChange={(event) => setFilter(event.target.value)}>
              <option value="">All references</option>
              <option value="unassigned">Unassigned</option>
               {(types ?? []).map((type) => <option key={type.id} value={type.id}>{type.name}{!type.enabled ? " (Disabled)" : ""}</option>)}
            </select>
          </div>}
        </div>
        <TabsContent value="library" forceMount hidden={activeTab !== "library"} className="space-y-4">
          {typesError || isError ? <div role="alert" className="rounded-lg border border-rose-200 bg-white p-4">
            <p>Unable to load references or board types.</p>
            <Button type="button" variant="outline" className="mt-2" onClick={() => { refetchTypes(); refetch(); }}>Retry</Button>
          </div> : isLoading || typesLoading ? <div role="status" aria-label="Loading references" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => <div key={item} className="space-y-3 rounded-xl border border-violet-100 bg-white p-2.5">
              <div className="aspect-square animate-pulse rounded-lg bg-violet-50" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              <div className="h-8 animate-pulse rounded bg-muted" />
            </div>)}
          </div>
            : !images?.length ? <p className="rounded-xl border border-violet-100 bg-white p-8 text-center text-sm text-slate-600">No references found for this filter.</p>
              : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {libraryPagination.items.map((image) => {
                  const actionPending = libraryActionPending(image.id);
                  const suggestionLoading = librarySuggestions[image.id]?.loading === true;
                  const inBoards = image.enabled !== false;
                   return <Card key={image.id} className="overflow-hidden rounded-xl border-violet-100 bg-white shadow-[0_5px_18px_-16px_rgba(50,34,90,.45)]" data-testid={`library-card-${image.id}`}>
                   <ReferenceImage sources={[image.previewUrl]} alt="Board inspiration reference" testId={`library-preview-${image.id}`} />
                   <CardContent className="space-y-2.5 p-3">
                     <div className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 ${inBoards ? "border-violet-100 bg-violet-50/45" : "border-slate-200 bg-slate-50"}`}>
                      <label htmlFor={`library-enabled-${image.id}`} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox id={`library-enabled-${image.id}`} checked={inBoards}
                          aria-label={`Use reference ${image.id} in boards`}
                          disabled={actionPending || suggestionLoading}
                          onCheckedChange={(checked) => updateLibraryEnabled(image, checked === true)} />
                         <span className="text-xs font-medium">Use in boards</span>
                      </label>
                       {actionPending ? <span role="status" className="flex items-center gap-1 text-[11px] text-slate-500">
                        <Loader2 className="h-3 w-3 animate-spin" />Saving
                       </span> : <span className={`text-[11px] ${inBoards ? "text-slate-500" : "font-medium text-amber-700"}`}>
                        {inBoards ? "Included in boards" : "Off"}
                      </span>}
                    </div>
                    {libraryEnabledErrors[image.id] && <p role="alert" data-testid={`library-enabled-error-${image.id}`} className="text-xs text-destructive">
                      Could not update board use: {libraryEnabledErrors[image.id]}
                    </p>}
                     <div className="flex flex-wrap items-center gap-1.5" aria-label="Assigned board types">
                       {image.typeIds.map((typeId) => {
                         const assigned = types?.find((type) => type.id === typeId);
                         return assigned ? <span key={typeId} className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-800" data-testid={`assigned-type-${image.id}-${typeId}`}>{assigned.name}</span> : null;
                       })}
                       {!image.typeIds.length && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500" data-testid={`unassigned-badge-${image.id}`}>Unassigned</span>}
                       {image.source === "performance" && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-semibold text-violet-800">Top performer</span>}
                       {image.source === "saved-ad" && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-semibold text-violet-800">My Ads</span>}
                     </div>
                     {image.performanceSnapshot && <p className="text-[11px] font-medium text-slate-600">{formatPerformanceMetricLabel(image.performanceSnapshot)}</p>}
                     <p className="text-[10px] text-slate-400">{image.width} × {image.height} · {image.mimeType}</p>
                     <details className="group rounded-lg border border-violet-100 bg-[#fcfbff]">
                       <summary data-testid={`library-controls-${image.id}`} className="flex cursor-pointer list-none items-center justify-between gap-2 px-2.5 py-2 text-xs font-semibold text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 [&::-webkit-details-marker]:hidden">
                         <span>Manage board types</span><span aria-hidden="true" className="text-violet-500 transition-transform group-open:rotate-45">+</span>
                       </summary>
                       <fieldset className="space-y-2 border-t border-violet-100 px-2.5 py-2.5" disabled={actionPending || suggestionLoading || confirmingLibraryIds.includes(image.id)}>
                         <legend className="sr-only">Assign board types for reference {image.id}</legend>
                         {!available.length && <p className="text-xs text-slate-500">No board types available yet.</p>}
                         {available.map((type) => <label key={type.id} className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
                           <Checkbox checked={image.typeIds.includes(type.id)} onCheckedChange={(checked) => {
                             const typeIds = checked === true ? [...new Set([...image.typeIds, type.id])] : image.typeIds.filter((id) => id !== type.id);
                             classifyImage(image, typeIds);
                           }} aria-label={`Assign ${type.name} to reference ${image.id}`} />
                           {type.name}
                         </label>)}
                       </fieldset>
                       {!image.typeIds.length && <div className="border-t border-violet-100 p-2.5">
                         <LibraryTypeSuggestions imageId={image.id} available={available} state={librarySuggestions[image.id]} disabled={actionPending || confirmingLibraryIds.includes(image.id)}
                           onSuggest={() => suggestTypes("library", image.id, { imageId: image.id })}
                           onConfirm={(typeId) => confirmLibrarySuggestion(image, typeId)} />
                       </div>}
                     </details>
                     <Button type="button" variant="outline" size="sm" className="h-8 w-full border-slate-200 text-xs text-slate-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700" disabled={remove.isPending || actionPending} onClick={() => {
                      if (window.confirm("Remove this reference from the library?")) remove.mutate(image);
                    }} data-testid={`delete-library-image-${image.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
                   </CardContent>
                </Card>;
                })}
              </div>}
          {!typesError && !isError && !isLoading && !typesLoading && <ReferencePagination {...libraryPagination} source="Library" />}
        </TabsContent>

        <TabsContent value="my-ads" forceMount hidden={activeTab !== "my-ads"} className="space-y-4">
          <MyAdsPanel key={brandId} brandId={brandId} enabled={activeTab === "my-ads"} available={available}
            typesLoading={typesLoading} typesError={typesError} refetchTypes={() => { refetchTypes(); }} />
        </TabsContent>

        {performanceEnabled && <TabsContent value="top-performers" forceMount hidden={activeTab !== "top-performers"} className="space-y-4">
          <TopPerformersPanel
            typesLoading={typesLoading}
            typesError={typesError}
            available={available}
            refetchTypes={() => { refetchTypes(); }}
            membershipsLoading={membershipsLoading}
            membershipsError={membershipsError}
            refetchMemberships={() => { refetchMemberships(); }}
            configLoading={performanceConfig.isLoading}
            configError={performanceConfig.isError}
            performanceEnabled={performanceEnabled}
            refetchConfig={() => { performanceConfig.refetch(); }}
            adsLoading={performanceAds.isLoading}
            adsError={performanceAds.isError}
            refetchAds={() => { if (performanceFenceRef.current.enabled) performanceAds.refetch(); }}
            topPerformers={topPerformers}
            pagination={performerPagination}
            metric={metric}
            since={performanceRange.since}
            until={performanceRange.until}
            windowDays={windowDays}
            onWindowDaysChange={(days) => {
              if (!performanceFenceRef.current.enabled) return;
              setWindowDays(days);
              setSelectedAds([]);
              setImportStatuses({});
              setImportSummary("");
            }}
            membershipByAd={membershipByAd}
            selectedAds={selectedAds}
            onSelectionChange={(adId, checked) => { if (performanceFenceRef.current.enabled) setSelectedAds((previous) => checked
              ? previous.includes(adId) ? previous : [...previous, adId]
              : previous.filter((id) => id !== adId)); }}
            cardTypeIds={cardTypeIds}
            onTypeChange={setPerformerType}
            performerSuggestions={performerSuggestions}
            onSuggestType={(ad) => ad.iterationSourceToken && suggestTypes("performer", ad.adId, { token: ad.iterationSourceToken })}
            onConfirmSuggestion={confirmPerformerSuggestion}
            importStatuses={importStatuses}
            importSummary={importSummary}
            importsBusy={importsBusy}
            onAddSelected={addSelectedToLibrary}
          />
        </TabsContent>}
      </Tabs>
    </section>
  );
}